import { getAccessProfileQueryOptions } from "@packages/api-client/hooks/authorizationController";
import { listEstablishmentsQueryOptions } from "@packages/api-client/hooks/establishmentController";
import { getMyOrganizationsQueryOptions } from "@packages/api-client/hooks/organizationController";
import { getCurrentUserQueryOptions } from "@packages/api-client/hooks/userController";
import { Action } from "@packages/shared-types";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { Sprout } from "lucide-react";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	isOnboardingEligible,
	onboardingSnapshotQueryOptions,
} from "@/api/onboarding";
import {
	scopedLivestockKpiSummaryQueryOptions,
	scopedMarketPriceQueryOptions,
	scopedOverdueTasksQueryOptions,
} from "@/api/scopedQueries";
import { EmptyDashboard } from "@/components/EmptyDashboard";
import { livestockAttentionQuery } from "@/components/livestock/attention/livestockAttention";
import { Skeleton } from "@/components/ui/skeleton";
import { WeatherPanel } from "@/components/weather/WeatherPanel";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useEstablishment } from "@/hooks/useEstablishment";
import { type FarmScope, useFarmScope } from "@/hooks/useFarmScope";
import {
	forgetSetupPageEscape,
	hasEscapedSetupPage,
} from "@/lib/onboarding/setupPageEscape";
import { AttentionCard } from "./-components/home/AttentionCard";
import { EquipmentAttentionCard } from "./-components/home/EquipmentAttentionCard";
import { FinancialSummaryCard } from "./-components/home/FinancialSummaryCard";
import { FirstWeightNudge } from "./-components/home/FirstWeightNudge";
import { HeaderCaptureButton } from "./-components/home/HeaderCaptureButton";
import { HomeGreeting } from "./-components/home/HomeGreeting";
import { HomeOverviewTour } from "./-components/home/HomeOverviewTour";
import { LivestockKpiCard } from "./-components/home/LivestockKpiCard";
import { MarketPriceCard } from "./-components/home/MarketPriceCard";
import {
	resolveGuideView,
	SetupGuide,
} from "./-components/home/onboarding/SetupGuide";
import { PaddocksCard } from "./-components/home/PaddocksCard";

// The open "What needs you today" group rides on the URL so the selection
// survives refresh/back and is shareable. `?farm=` stays declared at the
// parent org route (inherited here) — do NOT redeclare it.
const dashboardSearchSchema = z.object({
	attention: z
		.enum([
			"VACCINATION_DUE",
			"WEIGHING_OVERDUE",
			"PADDOCK_OVERSTAY",
			"OVER_CAPACITY",
			"CALVING_WINDOW",
		])
		.optional(),
	// Set by Help → "Ver guia de novo". Consumed once (the guide is restored to
	// EXPANDED) and then stripped, so a refresh does not re-open a guide the
	// user has since minimized.
	guide: z.literal("open").optional(),
	// Set by the final setup action and removed when the short sidebar tour ends.
	tour: z.literal("overview").optional(),
});

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/")({
	validateSearch: dashboardSearchSchema,
	// Every scoped prefetch depends on the farm scope, which rides on the
	// inherited `?farm=` search param — declare it so the loader re-runs when
	// the selected farm changes.
	loaderDeps: ({ search }: { search: { farm?: string } }) => ({
		farm: search.farm,
	}),
	loader: async ({ context, params, deps }) => {
		// Warm the data the home page reads so it paints without a flash.
		context.queryClient.ensureQueryData(getMyOrganizationsQueryOptions());
		context.queryClient.ensureQueryData(getCurrentUserQueryOptions());
		context.queryClient.ensureQueryData(
			listEstablishmentsQueryOptions(params.orgId),
		);
		// Warm the access profile so every card's permission gate resolves
		// from cache (the capture button reads it via a suspense query).
		context.queryClient.ensureQueryData(
			getAccessProfileQueryOptions({ organizationId: params.orgId }),
		);
		// The setup guide sits at the top of the page; warming its snapshot keeps
		// the no-farm state from flashing an empty home first.
		context.queryClient.ensureQueryData(
			onboardingSnapshotQueryOptions(params.orgId),
		);
		// establishmentIds is unused by the scoped queries (single-scope only
		// reads establishmentId), so an empty list is fine for the All-Farms arm.
		const scope: FarmScope = deps.farm
			? { kind: "single", establishmentId: deps.farm }
			: { kind: "all", establishmentIds: [] };
		// Permission-gated reads are prefetched, not ensured: a 403 for a role
		// without the grant must not reject the loader (the card renders nothing
		// either way). One KPI summary feeds the header pill, the herd tiles,
		// the paddocks and the unassigned row — React Query dedupes them.
		void context.queryClient.prefetchQuery(
			livestockAttentionQuery(params.orgId, scope),
		);
		void context.queryClient.prefetchQuery(
			scopedLivestockKpiSummaryQueryOptions(params.orgId, scope),
		);
		void context.queryClient.prefetchQuery(
			scopedOverdueTasksQueryOptions(params.orgId, scope),
		);
		// The cattle price's `staleTime` mirrors the route's one-hour
		// `Cache-Control`, so this warms at most once an hour.
		void context.queryClient.prefetchQuery(
			scopedMarketPriceQueryOptions(params.orgId, scope),
		);
	},
	component: Dashboard,
});

function Dashboard() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const navigate = useNavigate();
	const { guide, tour } = Route.useSearch();
	const { data: userOrganizations } = useSuspenseQuery(
		getMyOrganizationsQueryOptions(),
	);
	const { data: currentUser } = useSuspenseQuery(getCurrentUserQueryOptions());
	const scope = useFarmScope();
	const { establishments, isLoading: farmsLoading } = useEstablishment();

	// The guide is an assist, not a gate: its query never throws and a failure
	// simply means no guide (see `onboardingSnapshotQueryOptions`).
	const { data: onboardingSnapshot, isLoading: onboardingLoading } = useQuery(
		onboardingSnapshotQueryOptions(orgId),
	);
	const guideView = resolveGuideView(onboardingSnapshot);
	const guideVisible = guideView !== "HIDDEN";

	// `Ir para o início` on the setup page is an escape, not a preference: it
	// leaves without writing `guideState`, so home has to honour it or the user
	// bounces straight back. Read once per mount — sessionStorage is not
	// reactive, and re-reading it every render would be a synchronous storage hit
	// inside the render path. Asking for the guide again from Help clears it.
	const [setupEscaped] = useState(() => {
		if (guide === "open") forgetSetupPageEscape(orgId);
		return guide === "open" ? false : hasEscapedSetupPage(orgId);
	});

	// An `EXPANDED` guide is read on its own page (plan §7.5 rendered as the
	// full-page setup surface). The variants the setup page does not draw stay
	// here: the minimized bar, the recap, the completion state, the invited
	// member's first view and the read-only membership.
	const setupPageOwnsGuide =
		guideView === "EXPANDED" &&
		!setupEscaped &&
		isOnboardingEligible(onboardingSnapshot) &&
		onboardingSnapshot.entryKind !== "INVITATION" &&
		onboardingSnapshot.availableGoals.length > 0;

	// The summary pill reads the same KPI summary the herd tiles and paddocks
	// use — no extra request. It renders "—" until the read answers (or when
	// the role cannot see livestock at all).
	const can = useHasPermission(orgId);
	const { data: kpiSummary } = useQuery({
		...scopedLivestockKpiSummaryQueryOptions(orgId, scope),
		enabled: can(Action.VIEW_LIVESTOCK),
		throwOnError: false,
	});
	const stocking = kpiSummary?.stocking;
	const headCount =
		stocking?.status === "OK" ? stocking.total.activeHead : undefined;
	const grazingAreaHa =
		stocking?.status === "OK" && stocking.total.grazingAreaHa > 0
			? stocking.total.grazingAreaHa
			: undefined;

	const currentOrganization = userOrganizations?.find(
		(org) => org.id === orgId,
	);

	if (!currentOrganization) {
		return (
			<div className="p-4 sm:p-6">
				<div className="text-center">
					<h1 className="text-2xl font-bold text-base-content mb-2">
						{t("errors.notFound_Title")}
					</h1>
					<p className="text-sm sm:text-base text-base-content/70">
						{t("errors.notFound_Message")}
					</p>
				</div>
			</div>
		);
	}

	if (setupPageOwnsGuide) {
		return (
			<Navigate
				to="/app/org/$orgId/onboarding"
				params={{ orgId }}
				search={(prev) => prev}
				replace
			/>
		);
	}

	const hasFarms = establishments.length > 0;

	// The guide persists after the first farm exists (plan §7.6) — it leads the
	// page, ahead of every readout, until it is done. The first-weight nudge
	// occupies the same slot and renders only when the guide is HIDDEN, so the
	// two never stack. Suspense-isolated: it reads the access profile, which
	// suspends on a cold cache.
	const firstWeightNudge = (
		<Suspense fallback={null}>
			<FirstWeightNudge
				orgId={orgId}
				scope={scope}
				establishments={establishments}
				guideView={guideView}
			/>
		</Suspense>
	);

	const setupGuide = (
		<SetupGuide
			orgId={orgId}
			reopenRequested={guide === "open"}
			onReopenHandled={() =>
				navigate({
					to: "/app/org/$orgId",
					params: { orgId },
					search: (prev) => ({ ...prev, guide: undefined }),
					replace: true,
				})
			}
		/>
	);

	return (
		<div className="h-full flex flex-col bg-base-200/30">
			{tour === "overview" && (
				<HomeOverviewTour
					onFinish={() =>
						navigate({
							to: "/app/org/$orgId",
							params: { orgId },
							search: (prev) => ({ ...prev, tour: undefined }),
							replace: true,
						})
					}
				/>
			)}
			<div className="flex-1 overflow-auto">
				{/* The layout's <main> already pads the content column (p-3 / p-4),
				    so the page adds no side padding of its own — a second pad
				    just took width from the weather matrix and the tiles. */}
				<div className="mx-auto flex w-full max-w-[1280px] flex-col gap-3 pb-2 pt-1 sm:gap-4">
					<HomeGreeting
						firstName={currentUser.firstName ?? undefined}
						farmCount={farmsLoading ? undefined : establishments.length}
						headCount={headCount}
						grazingAreaHa={grazingAreaHa}
						action={
							// Only once a farm exists is there anything to record.
							hasFarms ? (
								<Suspense fallback={null}>
									<HeaderCaptureButton orgId={orgId} />
								</Suspense>
							) : undefined
						}
					/>

					{farmsLoading || onboardingLoading ? (
						<Skeleton className="h-48 w-full rounded-[0.875rem]" />
					) : !hasFarms ? (
						guideVisible ? (
							setupGuide
						) : (
							// No guide (an organization that predates onboarding, or one
							// whose owner dismissed it) still needs one way forward.
							<EmptyDashboard
								title={t("establishments.noEstablishments")}
								description={t("establishments.noEstablishmentsDescription")}
								icon={Sprout}
								action={{
									label: t("establishments.createFarm"),
									to: "/app/org/$orgId/settings/establishments",
									params: { orgId },
								}}
							/>
						)
					) : (
						/* The handoff's order, top to bottom, on every width: setup →
						   weather → herd + money → paddocks → what needs you today.
						   The farm scope is the shell's own selector (`?farm=`) — the
						   page adds no second one. The middle band is two columns
						   from `lg` (1.55fr : 1fr, heights balanced by the cards' own
						   content) and one stack below. Every section loads on its
						   own — the weather must never block the attention list. */
						<>
							{tour !== "overview" && setupGuide}
							{firstWeightNudge}

							<WeatherPanel orgId={orgId} scope={scope} />

							<div className="grid grid-cols-1 items-start gap-3 sm:gap-4 lg:grid-cols-[1.55fr_1fr]">
								<div className="flex min-w-0 flex-col gap-3 sm:gap-4">
									<Suspense fallback={null}>
										<MarketPriceCard orgId={orgId} scope={scope} />
									</Suspense>
									<Suspense fallback={null}>
										<LivestockKpiCard orgId={orgId} scope={scope} />
									</Suspense>
								</div>
								<div className="flex min-w-0 flex-col gap-3 sm:gap-4">
									{/* Renders only for VIEW_FINANCE holders; the gate lives
									    inside the card (suspense-isolated so it never blocks
									    the rest of the dashboard). */}
									<Suspense fallback={null}>
										<FinancialSummaryCard orgId={orgId} scope={scope} />
									</Suspense>
									{/* Fleet lane; self-hides when nothing needs a wrench. */}
									<Suspense fallback={null}>
										<EquipmentAttentionCard orgId={orgId} scope={scope} />
									</Suspense>
								</div>
							</div>

							<Suspense fallback={null}>
								<PaddocksCard orgId={orgId} scope={scope} />
							</Suspense>

							<Suspense fallback={null}>
								<AttentionCard orgId={orgId} scope={scope} />
							</Suspense>
						</>
					)}
				</div>
			</div>
		</div>
	);
}
